/* eslint-disable react/no-unstable-nested-components */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { Card, CardBody, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { RiArrowDownSLine } from 'react-icons/ri';
import { BiTrash } from 'react-icons/bi';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import { FSBTable } from 'components/common/FSBTable';
import FSBModal from 'components/common/FSBModal';
import Heading from 'components/Heading';
import { authContext } from 'contexts/AuthContext';
import { STAFF_LIST_QUERY } from 'pages/Staff/StaffgraphQL';
import { axiosDelete, axiosGetAPI, axiosPost } from 'services';
import { defaultDeleteFilter } from 'utils/constants';
import '../../Task.css';

const TASK_TEMPLATE_ENDPOINT =
  process.env.REACT_APP_REST_API_ENDPOINT ||
  (process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api/` : '/api/');

const AUTOMATED_TASK_BUTTON_GROUPS = [['Notify']];

const AUTOMATED_TASK_BUTTON_OPTIONS = AUTOMATED_TASK_BUTTON_GROUPS.flatMap(
  (group, groupIndex) =>
    group.map((buttonLabel, buttonIndex) => ({
      id: `${groupIndex}-${buttonIndex}-${buttonLabel}`,
      label: buttonLabel,
    })),
);

const AUTOMATED_TASK_ACTION_PROCESS_OPTIONS = [
  'DEMAND_RAISED',
  'BID_RECEIVED_FROM_SELLER',
  'OFFER_RAISED',
  'ORDER_CREATED',
];

const AUTOMATED_TASK_ACTION_PROCESS_SELECT_OPTIONS =
  AUTOMATED_TASK_ACTION_PROCESS_OPTIONS.map((option) => ({
    label: option,
    value: option,
  }));

const AUTOMATED_TASK_FORM_DEFAULTS = {
  actionProcess: '',
  taskOwnerRoleId: '',
  task: '',
  ert: '',
  reminder: '',
  escalationRoleId: '',
  userTaskText: '',
};

const MODAL_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? '#b9cbe0' : '#d7e0ec',
    boxShadow: 'none',
  }),
  menu: (base) => ({
    ...base,
    marginTop: 8,
    zIndex: 2000,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000,
  }),
};

const getAutomatedButtonLabel = (buttonId) =>
  AUTOMATED_TASK_BUTTON_OPTIONS.find((option) => option.id === buttonId)
    ?.label || buttonId;

const getUniqueAutomatedButtonLabels = (buttonIds = []) =>
  Array.from(
    new Set(
      buttonIds
        .map((buttonId) => getAutomatedButtonLabel(buttonId))
        .filter(Boolean),
    ),
  );

const getInitialAutomatedTaskForm = () => ({
  ...AUTOMATED_TASK_FORM_DEFAULTS,
});

const parseInteger = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getTimestampMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const normalizeAssigneeType = (roleName = '') => {
  const normalized = String(roleName)
    .trim()
    .replace(/\s+/g, '_')
    .toUpperCase();

  if (!normalized) return '';
  if (normalized.includes('PURCHASE')) return 'PURCHASE_MANAGER';
  if (normalized.includes('SALES')) return 'SALES_MANAGER';
  return normalized;
};

const SUPER_ADMIN_STAFF_FILTERS = {
  filter: [
    {
      key: 'name',
      value: 'SUPER_ADMIN',
      setAttribute: 'roles',
      type: 'STRING',
      op: 'IN',
    },
    { ...defaultDeleteFilter },
  ],
  pageNo: 1,
  pageSize: 20,
  sortCol: 'id',
  sortDirection: 'DESC',
  pagination: false,
};

const formatRoleName = (value = '') =>
  String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapTaskTemplateToRow = (template, index, pageNo, pageSize) => {
  const actionLabels = template?.actions
    ? String(template.actions)
      .split(',')
      .map((action) => formatRoleName(action.trim()))
      .filter(Boolean)
    : ['Notify'];

  return {
    id: template?.id || `automated-${pageNo}-${index}`,
    createdAt: template?.createdAt || template?.created_at || '',
    updatedAt: template?.updatedAt || template?.updated_at || '',
    createdAtMs: getTimestampMs(
      template?.createdAtMs ??
        template?.createdAt ??
        template?.updatedAtMs ??
        template?.updatedAt,
    ),
    actionProcess: template?.actionProcess || '',
    taskOwner: formatRoleName(template?.taskOwnerRoleName || ''),
    taskOwnerRoleId: template?.taskOwnerRoleId || '',
    task: template?.taskTitle || '',
    ert: template?.ertMinutes ?? '',
    reminder: template?.reminderMinutes ?? '',
    escalationTo: formatRoleName(template?.escalationRoleName || ''),
    escalationRoleId: template?.escalationRoleId || '',
    userTaskText: template?.userTaskDescription || '',
    buttons: [template?.actions || 'NOTIFY'],
    buttonLabels: actionLabels,
    serialNo: (pageNo - 1) * pageSize + index + 1,
  };
};

const getTaskTemplateDeleteUrl = (taskId) =>
  `${TASK_TEMPLATE_ENDPOINT}task-templates/${taskId}`;

const AutomatedTaskSettings = () => {
  const { auth } = useContext(authContext);
  const permissionNames = useMemo(() => {
    const permissionSet = new Set();
    const directPermissions = Array.isArray(auth?.data?.permissions)
      ? auth.data.permissions
      : [];
    directPermissions.forEach((permission) => {
      const normalized = `${permission?.permissionName || permission?.name || permission || ''}`
        .trim()
        .replace(/\s+/g, '_')
        .toUpperCase();
      if (normalized) permissionSet.add(normalized);
    });

    const roles = Array.isArray(auth?.data?.roles) ? auth.data.roles : [];
    roles.forEach((role) => {
      const rolePermissions = Array.isArray(role?.permissions)
        ? role.permissions
        : [];
      rolePermissions.forEach((permission) => {
        const normalized = `${permission?.permissionName || permission?.name || permission || ''}`
          .trim()
          .replace(/\s+/g, '_')
          .toUpperCase();
        if (normalized) permissionSet.add(normalized);
      });
    });

    return permissionSet;
  }, [auth?.data]);

  const canCreateTask = permissionNames.has('CREATE_TASK');
  const [isAutomatedTaskModalOpen, setIsAutomatedTaskModalOpen] =
    useState(false);
  const [automatedTaskForm, setAutomatedTaskForm] = useState(
    getInitialAutomatedTaskForm(),
  );
  const [automatedTasks, setAutomatedTasks] = useState([]);
  const [taskOwnerRoleOptions, setTaskOwnerRoleOptions] = useState([]);
  const [escalationRoleOptions, setEscalationRoleOptions] = useState([]);
  const [isTaskTemplateLoading, setIsTaskTemplateLoading] = useState(false);
  const [isTaskSaving, setIsTaskSaving] = useState(false);
  const [isTaskDeleting, setIsTaskDeleting] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [showDeleteRecord, setShowDeleteRecord] = useState(false);
  const [taskTemplatePaging, setTaskTemplatePaging] = useState({
    totalItems: 0,
    totalPages: 0,
  });
  const [expandedAutomatedTaskId, setExpandedAutomatedTaskId] = useState(null);
  const [filters, setFilters] = useState({ pageNo: 1, pageSize: 10 });

  const { data: superAdminStaffData } = useQuery(STAFF_LIST_QUERY, {
    variables: SUPER_ADMIN_STAFF_FILTERS,
    fetchPolicy: 'network-only',
    skip: !auth?.data?.token,
  });

  const fetchAutomatedTasks = useCallback(async () => {
    if (!auth?.data?.token) return;

    setIsTaskTemplateLoading(true);
    const response = await axiosGetAPI(
      `${TASK_TEMPLATE_ENDPOINT}task-templates/get-all?page=${filters.pageNo - 1}&size=${filters.pageSize}`,
      auth,
    );

    if (response?.isSuccessful) {
      const responseData = response.data || {};
      const paging = responseData.pagingResponse || {};
      const rows = Array.isArray(responseData.data) ? responseData.data : [];

      setAutomatedTasks(
        rows.map((task, index) =>
          mapTaskTemplateToRow(task, index, filters.pageNo, filters.pageSize),
        ),
      );
      setTaskTemplatePaging({
        totalItems: paging.totalItems || rows.length,
        totalPages: paging.totalPages || 0,
      });
    } else {
      toast.error(response?.message || 'Unable to fetch automated tasks.');
      setAutomatedTasks([]);
      setTaskTemplatePaging({ totalItems: 0, totalPages: 0 });
    }
    setIsTaskTemplateLoading(false);
  }, [auth, filters.pageNo, filters.pageSize]);

  const fetchTaskOwnerRoles = useCallback(async () => {
    if (!auth?.data?.token) return;

    const response = await axiosGetAPI(
      `${TASK_TEMPLATE_ENDPOINT}task-templates/task-owner-roles`,
      auth,
    );

    if (response?.isSuccessful) {
      const roleOptions = (Array.isArray(response.data) ? response.data : [])
        .filter((roleItem) => roleItem?.id)
        .map((roleItem) => ({
          value: roleItem.id,
          label: formatRoleName(roleItem.name),
          name: roleItem.name,
          assigneeType: normalizeAssigneeType(roleItem.name),
        }));
      setTaskOwnerRoleOptions(roleOptions);
    } else {
      toast.error(response?.message || 'Unable to fetch task owner roles.');
      setTaskOwnerRoleOptions([]);
    }
  }, [auth]);

  useEffect(() => {
    fetchAutomatedTasks();
  }, [fetchAutomatedTasks]);

  useEffect(() => {
    fetchTaskOwnerRoles();
  }, [fetchTaskOwnerRoles]);

  useEffect(() => {
    const staffUsers = superAdminStaffData?.usersFilter?.users || [];
    const superAdminRoles = staffUsers
      .flatMap((user) => user?.roles || [])
      .filter((roleItem) => roleItem?.name === 'SUPER_ADMIN' && roleItem?.id);
    const fallbackRoles = (auth?.data?.roles || []).filter(
      (roleItem) => roleItem?.name === 'SUPER_ADMIN' && roleItem?.id,
    );
    const role = superAdminRoles[0] || fallbackRoles[0];

    if (!role?.id) return;

    setEscalationRoleOptions([
      {
        value: role.id,
        label: formatRoleName(role.name),
        name: role.name,
      },
    ]);
  }, [auth?.data?.roles, superAdminStaffData?.usersFilter?.users]);

  useEffect(() => {
    setAutomatedTaskForm((prev) => ({
      ...prev,
      taskOwnerRoleId:
        prev.taskOwnerRoleId || taskOwnerRoleOptions[0]?.value || '',
      assigneeType:
        prev.assigneeType ||
        taskOwnerRoleOptions[0]?.assigneeType ||
        normalizeAssigneeType(taskOwnerRoleOptions[0]?.name),
      escalationRoleId:
        prev.escalationRoleId || escalationRoleOptions[0]?.value || '',
    }));
  }, [escalationRoleOptions, taskOwnerRoleOptions]);

  const handlePageChange = useCallback(({ pageNo, pageSize }) => {
    setFilters((prev) => ({ ...prev, pageNo, pageSize }));
  }, []);

  const updateAutomatedTaskForm = useCallback((field, value) => {
    setAutomatedTaskForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'taskOwnerRoleId'
        ? {
            assigneeType:
              taskOwnerRoleOptions.find(
                (option) => `${option.value}` === `${value}`,
              )?.assigneeType ||
              normalizeAssigneeType(
                taskOwnerRoleOptions.find(
                  (option) => `${option.value}` === `${value}`,
                )?.name,
              ),
          }
        : {}),
    }));
  }, [taskOwnerRoleOptions]);

  const resetAutomatedTaskForm = useCallback(() => {
    const defaultTaskOwner = taskOwnerRoleOptions[0];
    setAutomatedTaskForm({
      ...getInitialAutomatedTaskForm(),
      taskOwnerRoleId: defaultTaskOwner?.value || '',
      assigneeType:
        defaultTaskOwner?.assigneeType ||
        normalizeAssigneeType(defaultTaskOwner?.name),
      escalationRoleId: escalationRoleOptions[0]?.value || '',
    });
  }, [escalationRoleOptions, taskOwnerRoleOptions]);

  const openAutomatedTaskModal = useCallback(() => {
    if (!canCreateTask) {
      toast.error('You do not have permission to create automated tasks.');
      return;
    }
    setIsAutomatedTaskModalOpen(true);
  }, [canCreateTask]);

  const closeAutomatedTaskModal = useCallback(() => {
    setIsAutomatedTaskModalOpen(false);
    resetAutomatedTaskForm();
  }, [resetAutomatedTaskForm]);

  const handleAutomatedTaskSubmit = useCallback(async () => {
    const trimmedActionProcess = automatedTaskForm.actionProcess.trim();
    const trimmedTask = automatedTaskForm.task.trim();
    const trimmedUserTaskText = automatedTaskForm.userTaskText.trim();
    const taskOwnerRoleId = parseInteger(automatedTaskForm.taskOwnerRoleId);
    const escalationRoleId = parseInteger(automatedTaskForm.escalationRoleId);
    const selectedTaskOwnerRole = taskOwnerRoleOptions.find(
      (option) => `${option.value}` === `${automatedTaskForm.taskOwnerRoleId}`,
    );
    const assigneeType =
      selectedTaskOwnerRole?.assigneeType ||
      normalizeAssigneeType(selectedTaskOwnerRole?.name);

    if (!trimmedActionProcess || !trimmedTask || !trimmedUserTaskText) {
      toast.error('Action / Process, Task and User Task Text are required.');
      return;
    }

    if (!taskOwnerRoleId || !escalationRoleId) {
      toast.error('Task Owner and Escalation To are required.');
      return;
    }

    setIsTaskSaving(true);
    const response = await axiosPost(
      `${TASK_TEMPLATE_ENDPOINT}task-templates/create`,
      {
        actionProcess: trimmedActionProcess,
        taskTitle: trimmedTask,
        userTaskDescription: trimmedUserTaskText,
        ertMinutes: parseInteger(automatedTaskForm.ert),
        reminderMinutes: parseInteger(automatedTaskForm.reminder),
        escalationRoleId,
        taskOwnerRoleId,
        assigneeType,
        taskType: 'AUTOMATED',
        actions: 'NOTIFY',
      },
      { 'Content-Type': 'application/json' },
      auth,
    );

    setIsTaskSaving(false);

    if (response?.isSuccessful) {
      toast.success('Automated task added successfully.');
      closeAutomatedTaskModal();
      fetchAutomatedTasks();
      return;
    }

    toast.error(response?.message || 'Unable to add automated task.');
  }, [
    automatedTaskForm.actionProcess,
    automatedTaskForm.ert,
    automatedTaskForm.escalationRoleId,
    automatedTaskForm.reminder,
    automatedTaskForm.task,
    automatedTaskForm.taskOwnerRoleId,
    automatedTaskForm.userTaskText,
    auth,
    closeAutomatedTaskModal,
    fetchAutomatedTasks,
    taskOwnerRoleOptions,
  ]);

  const openDeleteConfirmation = useCallback((taskId) => {
    setDeleteRowId(taskId);
    setShowDeleteRecord(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    if (isTaskDeleting) return;

    setDeleteRowId(null);
    setShowDeleteRecord(false);
  }, [isTaskDeleting]);

  const handleDeleteTask = useCallback(async () => {
    if (!deleteRowId) return;

    setIsTaskDeleting(true);
    const response = await axiosDelete(
      getTaskTemplateDeleteUrl(deleteRowId),
      auth?.data?.token,
    );
    setIsTaskDeleting(false);

    if (response?.isSuccessful) {
      toast.success(
        response?.data?.message || 'Task Template deleted successfully',
      );
      closeDeleteConfirmation();
      fetchAutomatedTasks();
      return;
    }

    toast.error(
      response?.data?.message ||
        response?.message ||
        'Unable to delete automated task.',
    );
  }, [auth?.data?.token, closeDeleteConfirmation, deleteRowId, fetchAutomatedTasks]);

  const automatedTaskTableData = useMemo(
    () =>
      automatedTasks.map((task, index) => ({
        ...task,
        serialNo: task.serialNo || index + 1,
        buttonLabels: task.buttonLabels || getUniqueAutomatedButtonLabels(task.buttons),
      })),
    [automatedTasks],
  );

  const paginatedData = useMemo(() => {
    return automatedTaskTableData;
  }, [automatedTaskTableData]);

  const automatedTaskColumns = useMemo(
    () => [
      {
        accessorKey: 'serialNo',
        header: 'S.NO.',
        enableSorting: false,
        size: 72,
        minSize: 72,
        cell: ({ row }) => (
          <div className="automated-task-table-serial">
            {row.original.serialNo}
          </div>
        ),
      },
      {
        accessorKey: 'actionProcess',
        header: 'ACTION / PROCESS',
        enableSorting: false,
        size: 240,
        minSize: 190,
        cell: ({ row }) => (
          <div
            className="automated-task-cell-strong automated-task-ellipsis"
            title={row.original.actionProcess || '-'}
          >
            {row.original.actionProcess || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'task',
        header: 'TASK',
        enableSorting: false,
        size: 280,
        minSize: 220,
        cell: ({ row }) => (
          <div
            className="automated-task-cell-copy automated-task-ellipsis"
            title={row.original.task || '-'}
          >
            {row.original.task || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'userTaskText',
        header: 'USER TASK TEXT',
        enableSorting: false,
        size: 440,
        minSize: 320,
        cell: ({ row }) => (
          <div
            className="automated-task-cell-muted automated-task-ellipsis"
            title={row.original.userTaskText || 'No user task text added yet.'}
          >
            {row.original.userTaskText || 'No user task text added yet.'}
          </div>
        ),
      },
      {
        accessorKey: 'taskOwner',
        header: 'TASK OWNER',
        enableSorting: false,
        size: 180,
        minSize: 150,
        cell: ({ row }) => (
          <span className="automated-task-owner-pill">
            {row.original.taskOwner || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'ert',
        header: 'ERT',
        enableSorting: false,
        size: 78,
        minSize: 78,
        cell: ({ row }) => (
          <div className="automated-task-cell-copy">
            {row.original.ert || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'reminder',
        header: 'REMINDER',
        enableSorting: false,
        size: 88,
        minSize: 88,
        cell: ({ row }) => (
          <div className="automated-task-cell-copy">
            {row.original.reminder || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'escalationTo',
        header: 'ESCALATION',
        enableSorting: false,
        size: 180,
        minSize: 150,
        cell: ({ row }) => (
          <div
            className="automated-task-cell-copy automated-task-ellipsis"
            title={row.original.escalationTo || '-'}
          >
            {row.original.escalationTo || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'buttons',
        header: 'BUTTONS',
        enableSorting: false,
        size: 220,
        minSize: 180,
        cell: ({ row }) => (
          <div className="automated-task-buttons-cell">
            {row.original.buttonLabels.length ? (
              <div className="automated-task-pills-wrap d-flex align-items-center">
                <span className="automated-task-button-pill">
                  {row.original.buttonLabels[0]}
                </span>
                {row.original.buttonLabels.length > 1 && (
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="span"
                      className="automated-task-button-pill bg-light text-dark cursor-pointer ms-1 d-flex align-items-center"
                      style={{ border: '1px solid #d7e0ec', padding: '4px 8px' }}
                    >
                      +{row.original.buttonLabels.length - 1} <RiArrowDownSLine className="ms-1" />
                    </DropdownToggle>
                    <DropdownMenu end>
                      {row.original.buttonLabels.slice(1).map((buttonLabel) => (
                        <DropdownItem key={`${row.original.id}-${buttonLabel}`} disabled>
                          {buttonLabel}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                )}
              </div>
            ) : (
              <span className="automated-task-cell-muted">None</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'delete',
        header: 'DELETE',
        enableSorting: false,
        size: 84,
        minSize: 84,
        cell: ({ row }) => (
          <button
            type="button"
            className="btn btn-link text-danger p-0 d-inline-flex align-items-center justify-content-center"
            title="Delete"
            aria-label={`Delete automated task ${row.original.task || row.original.id}`}
            onClick={() => openDeleteConfirmation(row.original.id)}
          >
            <BiTrash size={18} />
          </button>
        ),
      },
    ],
    [openDeleteConfirmation],
  );

  const renderAutomatedTaskForm = () => (
    <div className="automated-task-panel">
      <div className="automated-task-form-grid">
        <div className="task-form-field automated-task-field-wide">
          <label className="form-label">Action / Process</label>
          <Select
            classNamePrefix="react-select"
            isSearchable={false}
            options={AUTOMATED_TASK_ACTION_PROCESS_SELECT_OPTIONS}
            value={AUTOMATED_TASK_ACTION_PROCESS_SELECT_OPTIONS.find(
              (option) => option.value === automatedTaskForm.actionProcess,
            )}
            onChange={(selectedOption) =>
              updateAutomatedTaskForm(
                'actionProcess',
                selectedOption?.value || '',
              )
            }
            placeholder="Select process..."
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={
              typeof document === 'undefined' ? null : document.body
            }
            styles={MODAL_SELECT_STYLES}
          />
        </div>

        <div className="task-form-field">
          <label className="form-label">ERT (Min.)</label>
          <input
            className="form-control"
            type="number"
            min="0"
            placeholder="Enter ERT"
            value={automatedTaskForm.ert}
            onChange={(event) =>
              updateAutomatedTaskForm('ert', event.target.value)
            }
          />
        </div>

        <div className="task-form-field">
          <label className="form-label">Reminder (Min)</label>
          <input
            className="form-control"
            type="number"
            min="0"
            placeholder="Enter reminder time"
            value={automatedTaskForm.reminder}
            onChange={(event) =>
              updateAutomatedTaskForm('reminder', event.target.value)
            }
          />
        </div>

        <div className="task-form-field">
          <label className="form-label">Task Owner</label>
          <Select
            classNamePrefix="react-select"
            isSearchable={false}
            options={taskOwnerRoleOptions}
            value={taskOwnerRoleOptions.find(
              (option) => option.value === automatedTaskForm.taskOwnerRoleId,
            )}
            onChange={(selectedOption) =>
              updateAutomatedTaskForm('taskOwnerRoleId', selectedOption?.value || '')
            }
            placeholder="Select task owner..."
            isDisabled={!taskOwnerRoleOptions.length}
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={
              typeof document === 'undefined' ? null : document.body
            }
            styles={MODAL_SELECT_STYLES}
          />
        </div>

        <div className="task-form-field">
          <label className="form-label">Escalation To</label>
          <Select
            classNamePrefix="react-select"
            isSearchable={false}
            options={escalationRoleOptions}
            value={escalationRoleOptions.find(
              (option) => option.value === automatedTaskForm.escalationRoleId,
            )}
            onChange={(selectedOption) =>
              updateAutomatedTaskForm(
                'escalationRoleId',
                selectedOption?.value || '',
              )
            }
            placeholder="Select escalation..."
            isDisabled={!escalationRoleOptions.length}
            menuPlacement="auto"
            menuPosition="fixed"
            menuPortalTarget={
              typeof document === 'undefined' ? null : document.body
            }
            styles={MODAL_SELECT_STYLES}
          />
        </div>

        <div className="task-form-field automated-task-field-wide">
          <label className="form-label">Task</label>
          <input
            className="form-control"
            type="text"
            placeholder="Enter task name"
            value={automatedTaskForm.task}
            onChange={(event) =>
              updateAutomatedTaskForm('task', event.target.value)
            }
          />
        </div>

        <div className="task-form-field automated-task-field-wide">
          <label className="form-label">User Task Text</label>
          <textarea
            className="form-control"
            rows="4"
            placeholder="Enter user-facing task text"
            value={automatedTaskForm.userTaskText}
            onChange={(event) =>
              updateAutomatedTaskForm('userTaskText', event.target.value)
            }
          />
        </div>

        <div className="task-form-field automated-task-field-wide">
          <label className="form-label">Buttons</label>
          <div className="automated-task-button-selection">
            <Select
              classNamePrefix="react-select"
              options={AUTOMATED_TASK_BUTTON_OPTIONS.map((opt) => ({
                label: opt.label,
                value: opt.id,
              }))}
              value={{ label: 'Notify', value: '0-0-Notify' }}
              placeholder="Select buttons..."
              styles={MODAL_SELECT_STYLES}
              isSearchable={false}
              isDisabled
              menuPlacement="auto"
              menuPortalTarget={
                typeof document === 'undefined' ? null : document.body
              }
            />
          </div>
        </div>
      </div>

      <div className="task-form-actions automated-task-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={closeAutomatedTaskModal}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleAutomatedTaskSubmit}
          disabled={isTaskSaving}
        >
          {isTaskSaving ? 'Saving...' : 'Save Task'}
        </button>
      </div>
    </div>
  );

  const renderAutomatedTaskMobileList = () => (
    <div className="automated-task-mobile-list">
      {paginatedData.map((task) => {
        const isExpanded = expandedAutomatedTaskId === task.id;
        return (
          <div className="automated-task-mobile-card" key={task.id}>
            <div className="automated-task-mobile-card__summary">
              <div className="automated-task-mobile-card__identity">
                <div className="automated-task-mobile-card__name">
                  {task.actionProcess || '-'}
                </div>
                <div className="automated-task-mobile-card__meta">
                  {`S.No ${task.serialNo} | ${task.taskOwner || '-'}`}
                </div>
              </div>
              <div className="automated-task-mobile-card__aside">
                <div className="automated-task-mobile-card__badges">
                  <span className="automated-task-owner-pill">
                    ERT: {task.ert || '-'}
                  </span>
                  <span className="automated-task-owner-pill">
                    Reminder: {task.reminder || '-'}
                  </span>
                </div>
                <button
                  type="button"
                  className={`automated-task-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''
                    }`}
                  onClick={() => {
                    setExpandedAutomatedTaskId((prevTaskId) => {
                      return prevTaskId === task.id ? null : task.id;
                    });
                  }}
                  aria-expanded={isExpanded}
                  aria-label={
                    isExpanded
                      ? 'Collapse automated task details'
                      : 'Expand automated task details'
                  }
                >
                  <RiArrowDownSLine />
                </button>
              </div>
            </div>

            {isExpanded ? (
              <div className="automated-task-mobile-card__detail-list">
                <div className="automated-task-mobile-card__detail-row">
                  <span className="automated-task-mobile-card__detail-label">
                    Task
                  </span>
                  <span className="automated-task-mobile-card__detail-value">
                    {task.task || '-'}
                  </span>
                </div>
                <div className="automated-task-mobile-card__detail-row">
                  <span className="automated-task-mobile-card__detail-label">
                    User Task Text
                  </span>
                  <span className="automated-task-mobile-card__detail-value">
                    {task.userTaskText || 'No user task text added yet.'}
                  </span>
                </div>
                <div className="automated-task-mobile-card__detail-row">
                  <span className="automated-task-mobile-card__detail-label">
                    Escalation
                  </span>
                  <span className="automated-task-mobile-card__detail-value">
                    {task.escalationTo || '-'}
                  </span>
                </div>
                <div className="automated-task-mobile-card__detail-row">
                  <span className="automated-task-mobile-card__detail-label">
                    Buttons
                  </span>
                  <span className="automated-task-mobile-card__detail-value">
                    {task.buttonLabels.length ? (
                      <span className="automated-task-mobile-card__pills d-flex align-items-center gap-1">
                        <span className="automated-task-button-pill">
                          {task.buttonLabels[0]}
                        </span>
                        {task.buttonLabels.length > 1 && (
                          <UncontrolledDropdown>
                            <DropdownToggle
                              tag="span"
                              className="automated-task-button-pill bg-light text-dark cursor-pointer d-flex align-items-center"
                              style={{ border: '1px solid #d7e0ec', padding: '4px 8px' }}
                            >
                              +{task.buttonLabels.length - 1} <RiArrowDownSLine className="ms-1" />
                            </DropdownToggle>
                            <DropdownMenu end>
                              {task.buttonLabels.slice(1).map((buttonLabel) => (
                                <DropdownItem key={`${task.id}-${buttonLabel}`} disabled>
                                  {buttonLabel}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        )}
                      </span>
                    ) : (
                      'None'
                    )}
                  </span>
                </div>
                <div className="automated-task-mobile-card__detail-row">
                  <span className="automated-task-mobile-card__detail-label">
                    Delete
                  </span>
                  <span className="automated-task-mobile-card__detail-value">
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 d-inline-flex align-items-center"
                      title="Delete"
                      aria-label={`Delete automated task ${task.task || task.id}`}
                      onClick={() => openDeleteConfirmation(task.id)}
                    >
                      <BiTrash size={18} />
                    </button>
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const renderAutomatedTaskContent = () => {
    return automatedTasks.length || isTaskTemplateLoading ? (
      <div className="automated-task-listing">
        {renderAutomatedTaskMobileList()}
        <div className="automated-task-desktop-listing">
          <FSBTable
            columns={automatedTaskColumns}
            data={paginatedData}
            loading={isTaskTemplateLoading}
            totalCount={taskTemplatePaging.totalItems}
            totalPages={taskTemplatePaging.totalPages}
            currentPageNo={filters.pageNo}
            pageSize={filters.pageSize}
            keyField="id"
            showSearch={false}
            showFilters={false}
            showDelete={false}
            showPagination
            handlePageChange={handlePageChange}
            striped={false}
            hover={false}
            bordered={false}
            wrapperClass="table-responsive word-break"
            tableWraperClass=""
            tableClass="automated-task-table"
          />
        </div>
      </div>
    ) : (
      <div className="automated-task-empty">
        No automated tasks available. Use Add Task to create one.
      </div>
    );
  };

  return (
    <div className="task-page automated-task-settings font-manrope">
      <div className="automated-task-section-header">
        <div className="automated-task-section-left">
          <Heading title="Task Management Setting" />
        </div>
        <div className="automated-task-section-actions">
          {canCreateTask && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={openAutomatedTaskModal}
            >
              Add Task
            </button>
          )}
        </div>
      </div>

      <Card className="border-0 card-box task-card-box page-card-box automated-task-card-box">
        <CardBody>{renderAutomatedTaskContent()}</CardBody>
      </Card>

      <DeleteConfirmationAlert
        open={showDeleteRecord}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        loading={isTaskDeleting}
        onConfirm={handleDeleteTask}
        onCancel={closeDeleteConfirmation}
        toggleAlert={closeDeleteConfirmation}
      />

      <FSBModal
        title="Add Automated Task"
        open={isAutomatedTaskModalOpen}
        toggleAlert={closeAutomatedTaskModal}
        onClose={closeAutomatedTaskModal}
        modalClass="right-drawer notification-drawer task-details-drawer automated-task-drawer"
        message=""
      >
        {renderAutomatedTaskForm()}
      </FSBModal>
    </div>
  );
};

export default AutomatedTaskSettings;
